/**
 * FlahaFAST Recipe Calculation Verification Standard
 *
 * Proves arithmetic + chemistry of the A/B (or single) stock recipe —
 * independent of agronomic “close to crop target” deviation control.
 *
 * Dual independent routes must agree:
 *   Route A — Direct final-volume: salt g → nutrient mg/L in final V
 *   Route B — Stock dilution: salt g → stock g/L → inject → working mg/L
 *
 * N semantics: N_NO3 = nitrate-N only; N_NH4 = ammonium-N only;
 * Total N = NO3-N + NH4-N (+ other quantified N if declared separately).
 * Plain "N" is never treated as only NO3 or only NH4.
 */

import {
  getSaltComposition,
  normalizeElementSymbol,
  isNitrateN,
  isAmmoniumN,
  isTotalN,
} from './saltElements';
import { normalizePurity } from './UnifiedHydroBuddyEngine';

export type GateStatus =
  | 'PASS'
  | 'FAIL'
  | 'WARNING'
  | 'CONDITIONAL'
  | 'NOT_CALCULABLE'
  | 'NOT_TESTED';

export interface VerificationGate {
  id: string;
  name: string;
  status: GateStatus;
  detail: string;
  maxAbsDiff?: number;
  tolerance?: number;
}

export interface SaltNutrientContribution {
  saltId: string;
  saltName: string;
  tank: 'A' | 'B' | 'single';
  symbol: string;
  normalized: string;
  saltMassG: number;
  percentage: number;
  purity: number;
  /** Route A: nutrient mg/L in final working volume */
  routeA_mgL: number;
  /** Route B: via stock conc × injection fraction */
  routeB_mgL: number;
  dualPathDiff: number;
  reverseMassG: number;
  reverseMassDiffPct: number;
}

export interface NutrientSumRow {
  symbol: string;
  waterMgL: number;
  fertilizerMgL: number;
  /** water + fertilizers (drain not yet wired) */
  finalMgL: number;
  /** Value reported by engine/report for same symbol, if any */
  reportedMgL?: number;
  reportDiff?: number;
}

export interface TankMassSummary {
  tank: 'A' | 'B' | 'single';
  totalMassG: number;
  stockVolumeL: number;
  stockConcentrationGL: number;
  saltCount: number;
}

export interface CalculationVerificationResult {
  overallStatus: 'Calculation Verified' | 'FAILED' | 'CONDITIONAL' | 'INCOMPLETE';
  mathematicallyVerified: boolean;
  chemicallyValid: boolean;
  gates: VerificationGate[];
  contributions: SaltNutrientContribution[];
  nutrientSums: NutrientSumRow[];
  nitrogen: {
    no3N: number;
    nh4N: number;
    otherN: number;
    totalN: number;
    sumForms: number;
    reconciliationError: number;
    status: GateStatus;
  };
  tanks: TankMassSummary[];
  inputs: {
    workingVolumeL: number;
    stockVolumeAL: number;
    stockVolumeBL: number;
    injectionRatioA: number;
    injectionRatioB: number;
    injectionFractionA: number;
    injectionFractionB: number;
  };
  summaryJson: Record<string, GateStatus | string>;
}

export interface VerificationSaltInput {
  id: string;
  name: string;
  amount: number; // grams in its stock tank
  purity?: number;
  elements?: any[];
  tankAssignment?: string;
  solubility?: number; // g/L
  formula?: string;
}

export interface VerificationInput {
  salts: VerificationSaltInput[];
  /** Final irrigation / working volume (L) */
  workingVolumeL: number;
  /** Tank A stock volume (L); same as stockVolumeL for single */
  stockVolumeAL: number;
  /** Tank B stock volume (L); 0 or = A for single */
  stockVolumeBL?: number;
  /** e.g. 100 for 1:100 */
  injectionRatioA: number;
  injectionRatioB?: number;
  waterElements?: Array<{ symbol?: string; concentration?: number }>;
  /** Optional engine/report achieved rows to cross-check */
  reportedConcentrations?: Array<{
    symbol: string;
    achieved: number;
  }>;
  solubilitySafetyFraction?: number; // default 0.85 block
}

const DUAL_PATH_TOL_MGL = 0.01;
const REVERSE_TOL_PCT = 0.05;
const N_RECON_TOL_MGL = 0.1;
const N_RECON_TOL_PCT = 0.1;
const REPORT_SUM_TOL_MGL = 0.05;

function waterMap(
  water?: Array<{ symbol?: string; concentration?: number }>
): Map<string, number> {
  const m = new Map<string, number>();
  for (const w of water || []) {
    const n = normalizeElementSymbol(w.symbol || '');
    if (!n) continue;
    m.set(n, (m.get(n) || 0) + (Number(w.concentration) || 0));
  }
  return m;
}

/**
 * Route A — Direct final volume (mg/L nutrient):
 *   nutrient_mgL = salt_g × (pct/100) × purity × 1000 / workingVolumeL
 *                = salt_g × pct × purity × 10 / workingVolumeL
 */
export function routeA_nutrientMgL(
  saltMassG: number,
  percentage: number,
  purity: number,
  workingVolumeL: number
): number {
  if (!(workingVolumeL > 0) || !(saltMassG > 0) || !(percentage > 0)) return 0;
  const p = normalizePurity(purity);
  return (saltMassG * percentage * p * 10) / workingVolumeL;
}

/**
 * Route B — Stock dilution:
 *   stock_gL = salt_g / stockVolumeL
 *   fertilizer_gL_working = stock_gL / injectionRatio
 *   nutrient_mgL = fertilizer_gL_working × (pct/100) × purity × 1000
 */
export function routeB_nutrientMgL(
  saltMassG: number,
  percentage: number,
  purity: number,
  stockVolumeL: number,
  injectionRatio: number
): number {
  if (
    !(stockVolumeL > 0) ||
    !(injectionRatio > 0) ||
    !(saltMassG > 0) ||
    !(percentage > 0)
  ) {
    return 0;
  }
  const p = normalizePurity(purity);
  const stockGL = saltMassG / stockVolumeL;
  const fertGLWorking = stockGL / injectionRatio;
  return fertGLWorking * (percentage / 100) * p * 1000;
}

function tankOf(s: VerificationSaltInput): 'A' | 'B' | 'single' {
  const t = (s.tankAssignment || 'A').toUpperCase();
  if (t === 'B') return 'B';
  if (t === 'SINGLE' || t === 'S') return 'single';
  return 'A';
}

/**
 * Run full calculation verification suite.
 */
export function verifyRecipeCalculation(
  input: VerificationInput
): CalculationVerificationResult {
  const workingVolumeL = Math.max(0.001, Number(input.workingVolumeL) || 1000);
  const stockVolumeAL = Math.max(0.001, Number(input.stockVolumeAL) || 10);
  const stockVolumeBL = Math.max(
    0.001,
    Number(input.stockVolumeBL ?? input.stockVolumeAL) || 10
  );
  const injectionRatioA = Math.max(1, Number(input.injectionRatioA) || 100);
  const injectionRatioB = Math.max(
    1,
    Number(input.injectionRatioB ?? input.injectionRatioA) || 100
  );
  const injectionFractionA = 1 / injectionRatioA;
  const injectionFractionB = 1 / injectionRatioB;
  const wMap = waterMap(input.waterElements);
  const gates: VerificationGate[] = [];
  const contributions: SaltNutrientContribution[] = [];

  // --- Gate: input consistency ---
  const inputOk =
    workingVolumeL > 0 &&
    stockVolumeAL > 0 &&
    injectionRatioA >= 1 &&
    (input.salts?.length ?? 0) > 0;
  gates.push({
    id: 'inputValidation',
    name: 'Input units and volumes',
    status: inputOk ? 'PASS' : 'FAIL',
    detail: inputOk
      ? `Working ${workingVolumeL} L · Tank A ${stockVolumeAL} L @ 1:${injectionRatioA}` +
        (stockVolumeBL !== stockVolumeAL || injectionRatioB !== injectionRatioA
          ? ` · Tank B ${stockVolumeBL} L @ 1:${injectionRatioB}`
          : '') +
        ` · ${input.salts.length} salt(s)`
      : 'Missing working volume, stock volume, injection ratio, or salts',
  });

  // Expected: stockVolume × injectionRatio ≈ workingVolume (per tank)
  const expectedWorkingA = stockVolumeAL * injectionRatioA;
  const volMatchA = Math.abs(expectedWorkingA - workingVolumeL) / workingVolumeL;
  gates.push({
    id: 'stockToWorkingVolume',
    name: 'Stock-to-working volume identity',
    status: volMatchA < 0.02 ? 'PASS' : volMatchA < 0.1 ? 'WARNING' : 'FAIL',
    detail: `Tank A: stock ${stockVolumeAL} L × 1:${injectionRatioA} ⇒ ${expectedWorkingA.toFixed(1)} L treated (declared working ${workingVolumeL} L)`,
    maxAbsDiff: Math.abs(expectedWorkingA - workingVolumeL),
    tolerance: 0.02 * workingVolumeL,
  });

  // --- Dual path + reverse + composition for each salt × element ---
  let dualPathFail = 0;
  let reverseFail = 0;
  let compositionWarn = 0;
  const fertByNorm = new Map<string, number>();

  for (const salt of input.salts) {
    const tank = tankOf(salt);
    const stockVol = tank === 'B' ? stockVolumeBL : stockVolumeAL;
    const inject = tank === 'B' ? injectionRatioB : injectionRatioA;
    const massG = Number(salt.amount) || 0;
    const purity = normalizePurity(salt.purity ?? 1);
    const composition = getSaltComposition(salt);

    if (composition.length === 0 && massG > 0) {
      compositionWarn++;
      continue;
    }

    // Sum of % check
    const sumPct = composition.reduce((s, e) => s + e.percentage, 0) * purity;
    if (sumPct > 100.5) {
      compositionWarn++;
    }

    for (const el of composition) {
      const norm = normalizeElementSymbol(el.symbol) || el.symbol;
      const routeA = routeA_nutrientMgL(massG, el.percentage, purity, workingVolumeL);
      const routeB = routeB_nutrientMgL(
        massG,
        el.percentage,
        purity,
        stockVol,
        inject
      );
      const dualDiff = Math.abs(routeA - routeB);
      if (dualDiff > DUAL_PATH_TOL_MGL) dualPathFail++;

      // Reverse: recovered mass from route A contribution
      const frac = (el.percentage / 100) * purity;
      const reverseMassG =
        frac > 0 ? (routeA * workingVolumeL) / (1000 * frac) : 0;
      const reverseDiffPct =
        massG > 0 ? (Math.abs(reverseMassG - massG) / massG) * 100 : 0;
      if (massG > 0 && reverseDiffPct > REVERSE_TOL_PCT) reverseFail++;

      fertByNorm.set(norm, (fertByNorm.get(norm) || 0) + routeA);

      contributions.push({
        saltId: salt.id,
        saltName: salt.name,
        tank,
        symbol: el.symbol,
        normalized: norm,
        saltMassG: massG,
        percentage: el.percentage,
        purity,
        routeA_mgL: routeA,
        routeB_mgL: routeB,
        dualPathDiff: dualDiff,
        reverseMassG,
        reverseMassDiffPct: reverseDiffPct,
      });
    }
  }

  gates.push({
    id: 'dualPathDilutionCheck',
    name: 'Dual-path dilution (direct vs stock inject)',
    status: dualPathFail === 0 ? 'PASS' : 'FAIL',
    detail:
      dualPathFail === 0
        ? `All ${contributions.length} salt×element pairs agree within ${DUAL_PATH_TOL_MGL} mg/L`
        : `${dualPathFail} pair(s) exceed ${DUAL_PATH_TOL_MGL} mg/L dual-path difference`,
    tolerance: DUAL_PATH_TOL_MGL,
  });

  gates.push({
    id: 'saltElementConversion',
    name: 'Salt-to-element conversion',
    status:
      contributions.length > 0
        ? compositionWarn > 0
          ? 'WARNING'
          : 'PASS'
        : 'FAIL',
    detail:
      contributions.length === 0
        ? 'No salt composition rows — cannot convert salt mass to nutrients'
        : compositionWarn > 0
          ? `${compositionWarn} salt(s) missing composition or sum % > 100 after purity`
          : `${contributions.length} nutrient contributions computed from guaranteed analysis × purity`,
  });

  gates.push({
    id: 'reverseCalculation',
    name: 'Reverse salt-mass recovery',
    status: reverseFail === 0 ? 'PASS' : 'FAIL',
    detail:
      reverseFail === 0
        ? `Recovered salt masses match within ${REVERSE_TOL_PCT}%`
        : `${reverseFail} recovery(ies) exceed ${REVERSE_TOL_PCT}% — wrong %/volume/double dilution`,
    tolerance: REVERSE_TOL_PCT,
  });

  // Mass conservation: element mass in stock = final mg/L × V / 1000
  let massConsFail = 0;
  for (const c of contributions) {
    const massInStockG = c.saltMassG * (c.percentage / 100) * c.purity;
    const massInWorkingG = (c.routeA_mgL * workingVolumeL) / 1000;
    const scale = Math.max(Math.abs(massInStockG), 1e-9);
    if (Math.abs(massInStockG - massInWorkingG) / scale > 1e-9) massConsFail++;
  }
  gates.push({
    id: 'massConservation',
    name: 'Element mass conservation',
    status:
      contributions.length === 0
        ? 'FAIL'
        : massConsFail === 0
          ? 'PASS'
          : 'FAIL',
    detail:
      massConsFail === 0 && contributions.length > 0
        ? 'Element mass in stock equals mass implied by final concentration × volume'
        : contributions.length === 0
          ? 'No contributions to check'
          : `${massConsFail} contribution(s) violate mass conservation`,
  });

  // Nutrient sums
  const reportedMap = new Map<string, number>();
  for (const r of input.reportedConcentrations || []) {
    const n = normalizeElementSymbol(r.symbol) || r.symbol;
    reportedMap.set(n, Number(r.achieved) || 0);
  }

  const allNorms = new Set<string>([
    ...fertByNorm.keys(),
    ...wMap.keys(),
    ...reportedMap.keys(),
  ]);

  const nutrientSums: NutrientSumRow[] = [];
  let sumMismatch = 0;
  for (const norm of allNorms) {
    if (!norm) continue;
    const waterMgL = wMap.get(norm) || 0;
    const fertilizerMgL = fertByNorm.get(norm) || 0;
    const finalMgL = waterMgL + fertilizerMgL;
    const reportedMgL = reportedMap.has(norm)
      ? reportedMap.get(norm)
      : undefined;
    const reportDiff =
      reportedMgL != null ? Math.abs(reportedMgL - finalMgL) : undefined;
    if (reportDiff != null && reportDiff > REPORT_SUM_TOL_MGL) sumMismatch++;
    nutrientSums.push({
      symbol: norm,
      waterMgL,
      fertilizerMgL,
      finalMgL,
      reportedMgL,
      reportDiff,
    });
  }

  gates.push({
    id: 'nutrientSummation',
    name: 'Nutrient summation (water + fertilizers)',
    status:
      reportedMap.size === 0
        ? 'NOT_TESTED'
        : sumMismatch === 0
          ? 'PASS'
          : 'FAIL',
    detail:
      reportedMap.size === 0
        ? 'No reported concentrations supplied for cross-check'
        : sumMismatch === 0
          ? 'Independent sum matches reported Result[] within tolerance'
          : `${sumMismatch} nutrient(s) disagree with reported Result[] (>${REPORT_SUM_TOL_MGL} mg/L)`,
    tolerance: REPORT_SUM_TOL_MGL,
  });

  // Nitrogen reconciliation
  let no3N = 0;
  let nh4N = 0;
  let otherN = 0;
  for (const row of nutrientSums) {
    if (isNitrateN(row.symbol) || row.symbol === 'N_NO3') no3N += row.finalMgL;
    else if (isAmmoniumN(row.symbol) || row.symbol === 'N_NH4') nh4N += row.finalMgL;
    else if (isTotalN(row.symbol) || row.symbol === 'N') otherN += row.fertilizerMgL; // plain N only from fert
  }
  // Water may have plain N
  otherN += wMap.get('N') || 0;

  const sumForms = no3N + nh4N;
  // Total N for verification = forms + other quantified N (plain N channel)
  // If both plain N and forms exist, plain N must not double-count forms
  // Total N := NO3-N + NH4-N when forms exist; else plain N only
  const formSum = no3N + nh4N;
  const totalN = formSum > 0 ? formSum : otherN;
  const reportedTotalN = reportedMap.get('N');
  let nStatus: GateStatus = 'PASS';
  let nDetail = `NO₃-N=${no3N.toFixed(2)} + NH₄-N=${nh4N.toFixed(2)} = ${formSum.toFixed(2)} mg/L (Total N)`;
  let reconError = 0;

  if (otherN > 0.05 && formSum > 0.05) {
    nStatus = 'FAIL';
    reconError = otherN;
    nDetail += ` · FAIL: plain N channel also ${otherN.toFixed(2)} mg/L — Total N must be NO₃-N+NH₄-N only when forms are quantified (no double-count)`;
  } else if (otherN > 0.05 && formSum <= 0.05) {
    nStatus = 'WARNING';
    nDetail = `Unlabeled total N only = ${otherN.toFixed(2)} mg/L — declare N_NO3 / N_NH4 % on fertilizers for form-correct verification`;
  } else if (reportedTotalN != null && formSum > 0) {
    reconError = Math.abs(reportedTotalN - formSum);
    const tol = Math.max(N_RECON_TOL_MGL, (formSum * N_RECON_TOL_PCT) / 100);
    if (reconError > tol) {
      nStatus = 'FAIL';
      nDetail += ` · reported total N ${reportedTotalN.toFixed(2)} ≠ NO₃+NH₄ ${formSum.toFixed(2)} (Δ ${reconError.toFixed(2)})`;
    } else {
      nDetail += ' · forms reconcile with reported total N';
    }
  } else if (formSum <= 0 && otherN <= 0) {
    nStatus = 'NOT_TESTED';
    nDetail = 'No nitrogen contributions found';
  } else {
    nDetail += ' · forms-only total (no conflicting plain N)';
  }

  gates.push({
    id: 'nitrogenReconciliation',
    name: 'Nitrogen-form reconciliation',
    status: nStatus,
    detail: nDetail,
    tolerance: N_RECON_TOL_MGL,
  });

  // Tank totals + solubility
  const tankMap = new Map<'A' | 'B' | 'single', { mass: number; n: number }>();
  for (const salt of input.salts) {
    const t = tankOf(salt);
    const cur = tankMap.get(t) || { mass: 0, n: 0 };
    cur.mass += Number(salt.amount) || 0;
    cur.n += 1;
    tankMap.set(t, cur);
  }
  const tanks: TankMassSummary[] = [];
  for (const [tank, v] of tankMap) {
    const vol = tank === 'B' ? stockVolumeBL : stockVolumeAL;
    tanks.push({
      tank,
      totalMassG: v.mass,
      stockVolumeL: vol,
      stockConcentrationGL: v.mass / vol,
      saltCount: v.n,
    });
  }

  const solFrac = input.solubilitySafetyFraction ?? 0.85;
  let solWarn = 0;
  let solBlock = 0;
  for (const salt of input.salts) {
    const tank = tankOf(salt);
    const vol = tank === 'B' ? stockVolumeBL : stockVolumeAL;
    const conc = (Number(salt.amount) || 0) / vol;
    const sol = Number(salt.solubility) || 0;
    if (sol > 0) {
      const util = conc / sol;
      if (util > solFrac) solBlock++;
      else if (util > 0.7) solWarn++;
    }
  }
  gates.push({
    id: 'solubility',
    name: 'Stock solubility safety',
    status:
      solBlock > 0 ? 'FAIL' : solWarn > 0 ? 'WARNING' : 'PASS',
    detail:
      solBlock > 0
        ? `${solBlock} salt(s) exceed ${solFrac * 100}% of listed solubility`
        : solWarn > 0
          ? `${solWarn} salt(s) in 70–${solFrac * 100}% solubility band`
          : 'No salt exceeds safety fraction of listed solubility (or solubility unknown)',
  });

  // A/B compatibility (simple ion rules)
  let compatStatus: GateStatus = 'PASS';
  let compatDetail = 'No Ca–phosphate / Ca–sulfate co-concentration in same tank detected';
  const byTank = { A: input.salts.filter((s) => tankOf(s) === 'A'), B: input.salts.filter((s) => tankOf(s) === 'B') };
  const tankHas = (list: VerificationSaltInput[], ion: string) =>
    list.some((s) =>
      getSaltComposition(s).some(
        (e) => normalizeElementSymbol(e.symbol) === normalizeElementSymbol(ion)
      )
    );
  for (const t of ['A', 'B'] as const) {
    const list = byTank[t];
    if (list.length === 0) continue;
    if (tankHas(list, 'Ca') && tankHas(list, 'P')) {
      compatStatus = 'CONDITIONAL';
      compatDetail = `Tank ${t}: Ca + P together in concentrate — high precipitation risk`;
    } else if (tankHas(list, 'Ca') && tankHas(list, 'S')) {
      if (compatStatus === 'PASS') compatStatus = 'WARNING';
      compatDetail = `Tank ${t}: Ca + S together — monitor concentration`;
    }
  }
  if (byTank.A.length === 0 && byTank.B.length === 0) {
    // single tank
    const all = input.salts;
    if (tankHas(all, 'Ca') && tankHas(all, 'P')) {
      compatStatus = 'CONDITIONAL';
      compatDetail = 'Single tank contains Ca + P — prefer A/B split';
    }
  }
  gates.push({
    id: 'tankCompatibility',
    name: 'A/B compatibility',
    status: compatStatus,
    detail: compatDetail,
  });

  gates.push({
    id: 'chargeBalance',
    name: 'Charge balance',
    status: 'NOT_CALCULABLE',
    detail:
      'Requires complete ionic forms (NO₃⁻, SO₄²⁻, H₂PO₄⁻, HCO₃⁻, etc.) — elemental P/S only is insufficient',
  });

  gates.push({
    id: 'measuredEcValidation',
    name: 'Measured EC validation',
    status: 'NOT_TESTED',
    detail: 'Lab/instrument EC of prepared batch not supplied',
  });

  // Overall
  const hardFails = gates.filter((g) => g.status === 'FAIL');
  const conditional = gates.filter((g) => g.status === 'CONDITIONAL' || g.status === 'WARNING');
  const mathIds = [
    'inputValidation',
    'dualPathDilutionCheck',
    'saltElementConversion',
    'reverseCalculation',
    'massConservation',
    'nutrientSummation',
    'nitrogenReconciliation',
  ];
  const mathGates = gates.filter((g) => mathIds.includes(g.id));
  const mathematicallyVerified = mathGates.every(
    (g) => g.status === 'PASS' || g.status === 'NOT_TESTED' || g.status === 'WARNING'
  ) && !mathGates.some((g) => g.status === 'FAIL');

  const chemicallyValid =
    !gates.some(
      (g) =>
        (g.id === 'solubility' || g.id === 'tankCompatibility') &&
        g.status === 'FAIL'
    );

  let overallStatus: CalculationVerificationResult['overallStatus'] = 'Calculation Verified';
  if (hardFails.length > 0) overallStatus = 'FAILED';
  else if (conditional.length > 0 || !chemicallyValid) overallStatus = 'CONDITIONAL';
  else if (!inputOk) overallStatus = 'INCOMPLETE';

  const summaryJson: Record<string, GateStatus | string> = {
    inputValidation: gates.find((g) => g.id === 'inputValidation')!.status,
    dualPathDilutionCheck: gates.find((g) => g.id === 'dualPathDilutionCheck')!.status,
    saltElementConversion: gates.find((g) => g.id === 'saltElementConversion')!.status,
    reverseCalculation: gates.find((g) => g.id === 'reverseCalculation')!.status,
    massConservation: gates.find((g) => g.id === 'massConservation')!.status,
    nutrientSummation: gates.find((g) => g.id === 'nutrientSummation')!.status,
    nitrogenReconciliation: gates.find((g) => g.id === 'nitrogenReconciliation')!.status,
    solubility: gates.find((g) => g.id === 'solubility')!.status,
    tankCompatibility: gates.find((g) => g.id === 'tankCompatibility')!.status,
    chargeBalance: 'NOT_CALCULABLE',
    measuredEcValidation: 'NOT_TESTED',
    overallStatus,
  };

  return {
    overallStatus,
    mathematicallyVerified:
      mathematicallyVerified && hardFails.filter((g) => mathIds.includes(g.id)).length === 0,
    chemicallyValid,
    gates,
    contributions,
    nutrientSums: nutrientSums.sort((a, b) => b.finalMgL - a.finalMgL),
    nitrogen: {
      no3N,
      nh4N,
      otherN,
      totalN,
      sumForms: formSum,
      reconciliationError: reconError,
      status: nStatus,
    },
    tanks,
    inputs: {
      workingVolumeL,
      stockVolumeAL,
      stockVolumeBL,
      injectionRatioA,
      injectionRatioB,
      injectionFractionA,
      injectionFractionB,
    },
    summaryJson,
  };
}
